import { Button } from '@unified-lk/ui'
import { formatDateTime } from '../../lib/format'
import type { StaffPatientVerificationCodesData } from '../../types/plans'
import {
  workspaceActionsClassName,
  workspaceEmptyClassName,
  workspaceFieldClassName,
  workspaceFieldLabelClassName,
  workspaceGridClassName,
  workspaceHeaderClassName,
  workspaceInputClassName,
  workspaceKickerClassName,
  workspaceMetricClassName,
  workspaceMutedTextClassName,
  workspacePanelClassName,
  workspaceSummaryGridClassName,
  workspaceTableCardRowClassName,
  workspaceTableClassName,
} from '../workspaceStyles'

export function PatientVerificationCodesSection({
  data,
  loading,
  error,
  phoneValue,
  onPhoneChange,
  onFilterSubmit,
  onRetry,
}: {
  data: StaffPatientVerificationCodesData | null
  loading: boolean
  error: string | null
  phoneValue: string
  onPhoneChange: (value: string) => void
  onFilterSubmit: () => void
  onRetry: () => void
}) {
  if (loading && !data) {
    return <div className={workspaceEmptyClassName}><p>Загружаем SMS-коды...</p></div>
  }

  if (!data) {
    return <div className={workspaceEmptyClassName}><p>{error ?? 'Раздел SMS-кодов пока недоступен.'}</p></div>
  }

  const phonesHidden = data.codes.some((entry) => entry.phone_hidden)

  return (
    <div className={workspaceGridClassName}>
      <section className={workspacePanelClassName}>
        <div className={workspaceHeaderClassName}>
          <div className="grid gap-2">
            <p className={workspaceKickerClassName}>Verification Codes</p>
            <h2 className="text-2xl font-semibold text-slate-900">SMS коды</h2>
            <p className={workspaceMutedTextClassName}>Служебный просмотр последних кодов подтверждения по номеру телефона или без фильтра.</p>
            {data.sources ? <p className="text-xs leading-5 text-slate-500">Source: `{data.sources.verification_codes}`</p> : null}
          </div>
          <div className={workspaceActionsClassName}>
            <Button variant="secondary" type="button" onClick={onRetry}>Обновить</Button>
          </div>
        </div>

        <div className={workspaceSummaryGridClassName}>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Всего</span><strong className="text-slate-900">{data.summary.total}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Активны</span><strong className="text-slate-900">{data.summary.active}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Истекли</span><strong className="text-slate-900">{data.summary.expired}</strong></div>
        </div>

        {phonesHidden ? (
          <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
            Телефоны скрыты: у пользователя нет права `patients_phone`.
          </div>
        ) : (
          <div className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>Фильтр по телефону</span>
            <div className={workspaceActionsClassName}>
              <input className={workspaceInputClassName} value={phoneValue} onChange={(event) => onPhoneChange(event.target.value)} placeholder="+7..." />
              <Button type="button" onClick={onFilterSubmit}>Применить</Button>
            </div>
          </div>
        )}
      </section>

      {error ? <div className="rounded-[20px] bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700"><p>{error}</p></div> : null}

      <section className={workspacePanelClassName}>
        <div className={workspaceTableClassName}>
          <div className="hidden grid-cols-[120px_minmax(0,1.1fr)_120px_180px_180px] gap-4 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 lg:grid">
            <span>Код</span>
            <span>Телефон</span>
            <span>PCODE</span>
            <span>Создан</span>
            <span>Истекает</span>
          </div>
          {data.codes.map((entry) => (
            <div key={`${entry.phone}-${entry.code}-${entry.created_at}`} className={`${workspaceTableCardRowClassName} lg:grid-cols-[120px_minmax(0,1.1fr)_120px_180px_180px]`}>
              <span><strong className="block text-slate-900">{entry.code}</strong></span>
              <span>{entry.phone_hidden ? 'Скрыт правами' : entry.phone}</span>
              <span>{entry.pcode ?? 'n/a'}</span>
              <span>{formatDateTime(entry.created_at)}</span>
              <span>
                <strong className="block text-slate-900">{formatDateTime(entry.expires_at)}</strong>
                <small className="text-slate-500">{entry.is_expired ? 'Истек' : 'Активен'}</small>
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
